import Text from "@components/CustomText";
import color from "@constants/color";
import metrics from "@constants/metrics";
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import {
  View,
  StyleSheet,
  RefreshControl,
  FlatList,
  ActivityIndicator,
  Animated,
  Image,
  TouchableOpacity,
} from "react-native";
import t from "@constants/localization";
import { useNavigation } from "@react-navigation/native";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import { format } from "date-fns";
import PaymentDetail from "./components/PaymentDetail";
import OverviewCard from "./components/OverviewCard";
import ListHeader from "./components/ListHeader";
import {
  getDeliveryDetailsOverview,
  getReturnDetailsOverview,
} from "@services/api";
import downArrowGif from "@assets/images/down.gif";

const PATH = "screens/MyDeliveries/Overview";
const TabEnum = {
  DELIVERY: "delivery",
  RETURN: "return",
};
const ROUTE_TYPES = {
  DELIVERY: "DELIVERY",
  PICKUP: "PICKUP",
  DELIVERY_PICKUP: "DELIVERY_PICKUP",
};

const BottomFooter = ({ cl_cx_otp }) => (
  <View style={styles.bottomContainer}>
    <Text variant="title_20" style={styles.title_20}>
      {t(`${PATH}/deliveryCode`, {
        code: cl_cx_otp,
      })}
    </Text>
  </View>
);

const DeliveryReturnOverview = (props) => {
  const {
    route: { params },
  } = props;

  const { spokeDetails = {}, routeType = "DELIVERY_PICKUP" } = params || {};
  const navigation = useNavigation();

  const cl_cx_otp = useMemo(
    () =>
      routeType === ROUTE_TYPES.DELIVERY ||
      routeType === ROUTE_TYPES.DELIVERY_PICKUP
        ? spokeDetails?.deliveryDetails?.deliveryBoyInfo?.cl_cx_otp
        : spokeDetails?.returnDetails?.deliveryBoyInfo?.cl_cx_otp,
    [routeType, spokeDetails],
  );

  const { spokeName } = spokeDetails;
  const [deliveryData, setDeliveryData] = useState([]);
  const [returnData, setReturnData] = useState([]);

  const [isLoading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const today = useMemo(() => format(new Date(), "yyyy-MM-dd"), []);

  const { showReturnData = true, showDeliveryData = true } = useMemo(() => {
    return routeType === ROUTE_TYPES.DELIVERY_PICKUP
      ? { showReturnData: true, showDeliveryData: true }
      : routeType === ROUTE_TYPES.DELIVERY
        ? { showReturnData: false, showDeliveryData: true }
        : { showReturnData: true, showDeliveryData: false };
  }, [routeType]);

  const [currentTab, setCurrentTab] = useState(
    showReturnData ? TabEnum.RETURN : TabEnum.DELIVERY,
  );

  const [showScrollButton, setShowScrollButton] = useState(true);
  const fadeAnim = useRef(new Animated.Value(1)).current;
  const flatListRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      if (showDeliveryData) {
        const res = await getDeliveryDetailsOverview({
          spoke_name: spokeName,
          edd: today,
        });
        setDeliveryData(res.data);
      }
      if (showReturnData) {
        const res = await getReturnDetailsOverview({
          spoke_name: spokeName,
          edd: today,
        });
        setReturnData(res.data);
      }
    } catch (error) {
      console.log(error);
    }
  }, [showDeliveryData, showReturnData, spokeName, today]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    })();
  }, [spokeName, today, fetchData]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  const handleScroll = (event) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isEndReached =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - 20;

    if (isEndReached && showScrollButton) {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setShowScrollButton(false));
    }
  };

  const getListData = useCallback(
    () => [
      { key: "paymentDetail" },
      { key: "tabBar" },
      ...(currentTab === TabEnum.DELIVERY
        ? (deliveryData?.items || []).map((item, index) => ({
            key: `delivery_${index}`,
            ...item,
          }))
        : (returnData?.items || []).map((item, index) => ({
            key: `return_${index}`,
            ...item,
          }))),
    ],
    [currentTab, deliveryData, returnData],
  );

  const handleItemPress = useCallback(
    (item) => {
      navigation.navigate("OverviewDetail", {
        title: item.title,
        type: item.type,
        id: deliveryData?.delivery_otp_id || returnData?.delivery_otp_id,
      });
    },
    [navigation, deliveryData, returnData],
  );

  const renderOverviewCard = useCallback(
    ({
      title,
      type,
      quantity,
      amount,
      amount_title,
      damaged_quantity,
      damaged_quantity_strip,
    }) => (
      <OverviewCard
        title={title}
        quantity={quantity}
        damaged_quantity={damaged_quantity}
        damaged_quantity_strip={damaged_quantity_strip}
        amount={amount || 0}
        amountLabel={amount_title || ""}
        onPress={() =>
          handleItemPress({
            title,
            type,
          })
        }
      />
    ),
    [handleItemPress],
  );

  const renderItem = useCallback(
    ({ item }) => {
      switch (item.key) {
        case "paymentDetail":
          return (
            <PaymentDetail
              payment_details={
                deliveryData?.payment_details || returnData?.payment_details
              }
              warning_lines={
                deliveryData?.warning_lines || returnData?.warning_lines
              }
            />
          );
        case "tabBar":
          return (
            <ListHeader
              currentTab={currentTab}
              setCurrentTab={setCurrentTab}
              showReturnData={showReturnData}
              showDeliveryData={showDeliveryData}
              TabEnum={TabEnum}
              PATH={PATH}
              isLoading={isLoading}
            />
          );
        default:
          if (
            item.key.startsWith("delivery_") ||
            item.key.startsWith("return_")
          ) {
            return renderOverviewCard(item);
          }
          return null;
      }
    },
    [
      currentTab,
      deliveryData?.payment_details,
      returnData?.payment_details,
      deliveryData?.warning_lines,
      returnData?.warning_lines,
      showReturnData,
      showDeliveryData,
      isLoading,
      renderOverviewCard,
    ],
  );

  const keyExtractor = useCallback((item) => item.key, []);

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={t(`${PATH}/title`)}
        headerColor="#fff"
        navigation={navigation}
        route={props.route}
      />
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={color.primary} />
        </View>
      ) : (
        <>
          <FlatList
            ref={flatListRef}
            data={getListData()}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            stickyHeaderIndices={[1]}
            refreshControl={
              <RefreshControl refreshing={refreshing} onRefresh={refresh} />
            }
            initialNumToRender={10}
            maxToRenderPerBatch={10}
            windowSize={5}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
            ListFooterComponent={
              <BottomFooter
                cl_cx_otp={
                  returnData.cl_cx_otp || deliveryData.cl_cx_otp || cl_cx_otp
                }
              />
            }
          />
          {showScrollButton && (
            <Animated.View
              style={[
                {
                  opacity: fadeAnim,
                },
              ]}
            >
              <TouchableOpacity
                activeOpacity={0.8}
                style={styles.floatingButton}
                onPress={() => {
                  flatListRef.current?.scrollToEnd({ animated: true });
                }}
              >
                <Text style={styles.buttonText}>{t(`${PATH}/otpMsg`)}</Text>
                <Image source={downArrowGif} style={styles.downArrow} />
              </TouchableOpacity>
            </Animated.View>
          )}
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.bgcolor,
  },
  bottomContainer: {
    paddingVertical: metrics.padding.md,
    backgroundColor: color.polarGreen,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 20,
    bottom: 0,
    width: "100%",
  },
  title_20: {
    color: color.white,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  floatingButton: {
    flexDirection: "row",
    alignItems: "center",
    position: "absolute",
    bottom: 100,
    alignSelf: "center",
    backgroundColor: color.black,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    elevation: 5,
    height: 38,
  },
  downArrow: {
    width: 34,
    height: 34,
    top: -2,
    right: -2,
  },
  buttonText: {
    color: color.white,
    lineHeight: 20,
    marginLeft: 6,
  },
});

export default DeliveryReturnOverview;
